import { TourService } from "./tour.service";
import Showtime from 'showtime.js';
import { BrowserUtilsService } from "../util/browserUtils.service";

declare let window: any;

export class TourOptions {
    public tourService: TourService;
    public browserUtilsService: BrowserUtilsService;
    public onNavigateBackwardsCallback: (event: TourNavigationEvent) => Promise<void>;
    public onNavigateForwardCallback: (event: TourNavigationEvent) => Promise<void>;
    public onQuitCallback: (event: TourNavigationEvent) => Promise<void>;
    public forceUndone: boolean;

    constructor(tourService: TourService,
        browserUtilsService: BrowserUtilsService,
        onNavigateBackwardsCallback: (event: TourNavigationEvent) => Promise<void>,
        onNavigateForwardCallback: (event: TourNavigationEvent) => Promise<void>,
        onQuitCallback: (event: TourNavigationEvent) => Promise<void>,
        forceUndone: boolean = false) {
        this.tourService = tourService;
        this.browserUtilsService = browserUtilsService;
        this.onNavigateBackwardsCallback = onNavigateBackwardsCallback;
        this.onNavigateForwardCallback = onNavigateForwardCallback;
        this.onQuitCallback = onQuitCallback;
        this.forceUndone = forceUndone;
    }
}

export abstract class Step {
    public name: string;
    constructor(name: string) {
        this.name = name;
    }
}

export class ShowStepOptions {
    public element: any;
    public content: string;
    public placement: string;
    public padding: number;

    constructor(element: any, content: string, placement: string, padding: number = 5) {
        this.element = element;
        this.content = content;
        this.placement = placement;
        this.padding = padding;
    }
}

export class ShowStep extends Step {
    public options: ShowStepOptions;

    constructor(name: string, options: ShowStepOptions) {
        super(name);
        this.options = options;
    }
}

export class ModalStepOptions {
    public message: string;

    constructor(message: string) {
        this.message = message;
    }
}

export class ModalStep extends Step {
    public options: ModalStepOptions;

    constructor(name: string, options: ModalStepOptions) {
        super(name);
        this.options = options;
    }
}

export class TourNavigationEvent {
    public sourceStep: string;
    public destinationStep: string;

    constructor(source: string, destination: string) {
        this.sourceStep = source;
        this.destinationStep = destination;
    }
}

export abstract class Tour<TOptions extends TourOptions> {
    private options: TOptions;
    public abstract getClassName(): string;
    public abstract getVersion(): number;
    private steps: Step[] = [];

    constructor(options: TOptions) {
        this.options = options;
        if (options && options.tourService && options.browserUtilsService) {
            if (!options.browserUtilsService.isMobileBrowser()) this.setup(options);
        }
    }

    protected abstract setup(options: TOptions): void;

    protected linkShow(name: string, options: ShowStepOptions) {
        this.steps.push(new ShowStep(name, options));
    }

    protected linkModal(name: string, options: ModalStepOptions) {
        this.steps.push(new ModalStep(name, options));
    }

    public start() {
        if (this.steps.length > 0) {
            let tour = new Showtime({
                pointerEvents: 'all',
                popoverTimer: 1100
            });
            this.build(tour);

            this.options.onNavigateForwardCallback(new TourNavigationEvent(null, this.steps[0].name));
            tour.start();
        }
        else {
            this.options.tourService.raiseTourEnded();
        }
    }

    protected build(tour: any) {
        for (let i = 0; i < this.steps.length; ++i) {
            let step = this.steps[i];
            let previous = i == 0 ? null : this.steps[i - 1];
            let next = i == (this.steps.length - 1) ? null : this.steps[i + 1];

            if (step instanceof ShowStep) {
                let buttons = [];

                buttons.push({
                    label: "Finalizar",
                    click: async () => {
                        if (await this.options.onQuitCallback) await this.options.onQuitCallback(new TourNavigationEvent(step.name, null));
                        this.options.tourService.setTourState(this.getClassName(), true, this.getVersion());
                        tour.quit();
                    }
                });

                if (previous) {
                    buttons.push({
                        label: "Anterior",
                        click: async () => {
                            if (this.options.onNavigateBackwardsCallback) await this.options.onNavigateBackwardsCallback(new TourNavigationEvent(step.name, previous.name));
                            tour.previous();
                        }
                    });
                }

                if (next) {
                    buttons.push({
                        label: "Siguiente",
                        click: async () => {
                            if (this.options.onNavigateForwardCallback) await this.options.onNavigateForwardCallback(new TourNavigationEvent(step.name, next.name));
                            tour.next();
                        }
                    });
                }

                tour.show({
                    element: step.options.element,
                    content: step.options.content,
                    placement: step.options.placement,
                    padding: step.options.padding,
                    buttons: buttons
                });
            } else if (step instanceof ModalStep) {
                let tourCallbacks = window[this.getClassName()];
                if (!tourCallbacks) tourCallbacks = {};

                let modalCallbackHtml = '<div align="right">';

                modalCallbackHtml += '<p class="popBtn" onclick="window.{0}.{1}_onQuit();">Finalizar</p>'.format(this.getClassName(), step.name);
                tourCallbacks[step.name + "_onQuit"] = async () => {
                    if (this.options.onQuitCallback) await this.options.onQuitCallback(new TourNavigationEvent(step.name, null));
                    this.options.tourService.setTourState(this.getClassName(), true, this.getVersion());
                    tour.quit();
                }

                if (previous) {
                    modalCallbackHtml += '<p class="popBtn" onclick="window.{0}.{1}_onPrev();">Anterior</p>'.format(this.getClassName(), step.name);
                    tourCallbacks[step.name + "_onPrev"] = async () => {
                        if (this.options.onNavigateBackwardsCallback) await this.options.onNavigateBackwardsCallback(new TourNavigationEvent(step.name, previous.name));
                        let modal = document.querySelector(".chain_modal");
                        if (modal) modal.remove();
                        tour.previous();
                    };
                }

                if (next) {
                    modalCallbackHtml += '<p class="popBtn" onclick="window.{0}.{1}_onNext();">Siguiente</p>'.format(this.getClassName(), step.name);
                    tourCallbacks[step.name + "_onNext"] = async () => {
                        if (this.options.onNavigateForwardCallback) await this.options.onNavigateForwardCallback(new TourNavigationEvent(step.name, next.name));
                        tour.next();
                    }
                }

                modalCallbackHtml += '</div>';

                window[this.getClassName()] = tourCallbacks;

                tour.modal({
                    message: step.options.message + modalCallbackHtml,
                    closeButton: false
                });

            } else throw Error("Unknown step type");
        }
    }
}
